import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../config/defaultLimit';
import { EditFacilityTag, SelectedFacilityTag } from '../../../interface/facility-tag.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import { fetchAllStationsList } from '../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import { fetchFacilityTags } from '../../../redux/slices/Partners/FacilityTagSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { stringValidation } from '../../../shared-functions/Validation';
import facilityTagApi from '../../../utils/api/partner/facility-tag';

interface EditFacilityProperties {
  handleCancel: () => void;
  handleSuccess: () => void;
  selectedFacilityTag: SelectedFacilityTag;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Facility Tag Add page function component
 * @param {any} properties - The properties
 */
function EditFacilityTagComponent(properties: EditFacilityProperties) {
  const { t } = useTranslation('partnerFacilityManagement');
  const dispatch = useAppDispatch();
  const { filters, sortField, sortBy } = useAppSelector((state) => state.partnersFacilityTags);
  const { handleCancel, handleSuccess, selectedFacilityTag } = properties;
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });
  const { partnerStations } = useAppSelector((state) => state.partnersApprovedLocation);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const chargingStationIds = selectedFacilityTag?.chargingStations?.map((station) => station._id);
  const [facilityDetails, setFacilityDetails] = useState<EditFacilityTag>({
    facilityTagName: selectedFacilityTag.facilityTagName,
    address: selectedFacilityTag?.address,
    chargingStations: chargingStationIds,
  });

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setFacilityDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (!stringValidation(1, 100, facilityDetails.facilityTagName)) {
      return;
    }

    setFormStatus({
      showFormErrors: true,
      buttonLoader: true,
    });

    const request: EditFacilityTag = {
      facilityTagName: facilityDetails.facilityTagName,
      address: facilityDetails.address,
      chargingStations: facilityDetails.chargingStations,
    };

    facilityTagApi
      .editFacilityTag(selectedFacilityTag?._id || '', request)
      .then(() => {
        const queryParameters = GetParametersFromCommonFilters(filters);
        dispatch(
          fetchFacilityTags({
            ...queryParameters,
            sortField,
            sortBy,
            page: DefaultApiLimit.page,
            limit: DefaultApiLimit.limit,
          }),
        );
        setFormStatus({
          showFormErrors: false,
          buttonLoader: false,
        });
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          content: (
            <SuccessNotification
              title={t('facilityUpdated')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleSuccess}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setFormStatus({
          showFormErrors: false,
          buttonLoader: false,
        });
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle the fetchData function
   */
  const fetchData = () => {
    dispatch(fetchAllStationsList());
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="partner-edit-facility-tag">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />

      {/* scrollable form area */}
      <div className="partner-edit-facility-tag__form">
        <CustomInput
          name="facilityTagName"
          inputLabel={t('facilityName')}
          type="text"
          placeholder={t('enterFacilityName')}
          valueRequired
          value={facilityDetails.facilityTagName}
          onChangeValue={handleInputChange}
          error={
            formStatus.showFormErrors && !stringValidation(1, 100, facilityDetails.facilityTagName)
          }
          errorMessage={t('facilityNameRequired')}
        />

        <MultiSelectDropdown
          placeholderFilled={t('station')}
          valueRequired
          title={t('selectStation')}
          options={partnerStations}
          name="chargingStations"
          placeholder={t('selectStation')}
          search
          selectedValues={facilityDetails.chargingStations}
          onSelectionChange={handleInputChange}
          error={formStatus.showFormErrors && !facilityDetails.chargingStations.length}
          errorMessage={t('stationRequired')}
          selectedChips
          limitChips={2}
        />

        <div className="partner-edit-facility-tag__form-field">
          <CustomInput
            name="address"
            inputLabel={t('address')}
            type="text"
            placeholder={t('enterAddress')}
            value={facilityDetails.address || ''}
            onChangeValue={handleInputChange}
          />
        </div>
      </div>

      {/* 🔴 Footer fixed bottom */}
      <div className="partner-edit-facility-tag__footer">
        <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleCancel} />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={submit}
          disableButton={formStatus.buttonLoader}
          loading={formStatus.buttonLoader}
        />
      </div>
    </div>
  );
}

export default EditFacilityTagComponent;
